import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  BenefitFlagPipe,
  ProductCalendarLabelComponent,
  ProductPlatformCellComponent,
  ProductTooltipComponent,
} from '@mkp/product/ui';
import {
  BEST_VALUE_PRODUCTS,
  JOBUP_LITE_PRODUCT_AVAILABLE_ITEMS,
  LITE_PRODUCT_AVAILABLE_SUBCATEGORIES,
  Product,
  ProductCode,
  ProductFeature,
  ProductOfferingBenefit,
  ProductOfferingFeature,
  ProductOfferingPlatform,
  ProductPlatformGroup,
} from '@mkp/shared/models';
import { Button, ButtonColor, ButtonComponent, ButtonSize } from '@mkp/shared/ui-library';
import { AssetComponent } from '@mkp/shared/ui-asset';
import { isLiteProduct } from '@mkp/shared/util-product';
import { DataTestDirective } from '@mkp/tracking/feature-tracking';
import { TranslateModule } from '@ngx-translate/core';

export type ProductOfferingModalType = {
  product: Product;
  benefits: ProductOfferingBenefit[];
  platforms: ProductOfferingPlatform[];
  isReadMode: boolean;
  bestValueTranslation: string;
  platform: ProductPlatformGroup;
};

@Component({
  selector: 'mkp-explain-benefits',
  templateUrl: './explain-benefits.component.html',
  styleUrl: './explain-benefits.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    AssetComponent,
    BenefitFlagPipe,
    ButtonComponent,
    MatDialogModule,
    DataTestDirective,
    TranslateModule,
    ProductTooltipComponent,
    ProductCalendarLabelComponent,
    ProductPlatformCellComponent,
  ],
})
export class ExplainBenefitsComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<ExplainBenefitsComponent>>(MatDialogRef);

  product: Product;
  benefits: ProductOfferingBenefit[];
  platforms: ProductOfferingPlatform[];
  isReadMode = false;
  bestValueTranslation: string;

  selectConfig: Button = {
    size: ButtonSize.Medium,
    color: ButtonColor.Primary,
  };
  isBestValue = false;
  platformMapping: ProductFeature[] = [];
  benefitMapping: ProductFeature[] = [];
  calendarFeature: ProductFeature | undefined;
  ProductCode = ProductCode;
  platform: ProductPlatformGroup;
  LITE_PRODUCT_AVAILABLE_SUBCATEGORIES = LITE_PRODUCT_AVAILABLE_SUBCATEGORIES;
  JOBUP_LITE_PRODUCT_AVAILABLE_ITEMS = JOBUP_LITE_PRODUCT_AVAILABLE_ITEMS;

  constructor() {
    const data = inject<ProductOfferingModalType>(MAT_DIALOG_DATA);

    this.product = data.product;
    this.benefits = data.benefits;
    this.platforms = data.platforms;
    this.isReadMode = data.isReadMode;
    this.bestValueTranslation = data.bestValueTranslation;
    this.platform = data.platform;
  }

  ngOnInit(): void {
    this.isBestValue = BEST_VALUE_PRODUCTS.includes(this.product.code as ProductCode);
    this.calendarFeature = this.product.features.find(({ id }) => id.includes('calendar'));
    this.setFeatureMapping();
  }

  close(isSelected: boolean): void {
    this.dialogRef.close(isSelected);
  }

  private setFeatureMapping() {
    if (this.benefits?.length && this.product) {
      this.benefitMapping = this.setFeatures(this.benefits);

      if (isLiteProduct(this.product.code) && this.calendarFeature) {
        this.benefitMapping.unshift({
          id: this.calendarFeature.id,
          title: this.calendarFeature.translation,
          description: this.calendarFeature.description,
        } as ProductFeature);
      }
    }
    if (this.platforms?.length && this.product) {
      this.platformMapping = this.setFeatures(this.platforms);
    }
  }

  private setFeatures(features: ProductOfferingFeature[]): ProductFeature[] {
    return features.reduce((acc: ProductFeature[], productFeature: ProductOfferingFeature) => {
      const feature = this.product.features.find((f) => [f.parent, f.id].includes(productFeature));
      return feature ? [...acc, feature] : acc;
    }, []);
  }
}
